import { Component, ElementRef, OnInit } from '@angular/core';
import { AbstractControl, FormGroup, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DialogCambiosSinGuardarComponent } from '@views/genericos/dialog-cambios-sin-guardar/dialog-cambios-sin-guardar.component';
import { BaseDto } from '@models/base/baseDto.model';
import { TiposFolios } from '@models/base/identificadores.model';
import { LocalidadDto } from '@models/localidad-provincia/localidadDto.model';
import { LocalidadBasicDto } from '@models/localidad-provincia/localidadBasicDto.model';
import { PropiedadDto } from '@models/propiedades/propiedadDto.model';
import { TipoPropiedadDto } from '@models/propiedades/tipoPropiedadDto.model';
import { UpdatePropiedadRequest } from '@models/propiedades/updatePropiedadRequest.model';
import { PropiedadesService } from '@services/propiedades/propiedades.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { SubtipoPropiedadService } from '@services/subtiposPropiedad/subtipo-propiedad.service';
import { TiposFolioService } from '@services/tipos-folio/tipos-folio.service';
import { isFalsy } from 'utility-types';

@Component({
  selector: 'app-form-update-propiedad',
  templateUrl: './form-update-propiedad.component.html',
  styleUrls: ['./form-update-propiedad.component.css']
})
export class FormUpdatePropiedadComponent implements OnInit {
  propiedadForm!: FormGroup
  idPropiedad: number = 0;
  formBuilder: FormBuilder = new FormBuilder;
  idPersona: any;
  propiedadDto: PropiedadDto = new PropiedadDto
  idTipoFolio: any;
  localidad: LocalidadBasicDto = new LocalidadBasicDto;
  get f() { return this.propiedadForm.controls; }
  lstSubtiposPropiedad: BaseDto[] = []
  lstTiposPropiedad: TipoPropiedadDto[] = []
  lstPropiedadClases: BaseDto[] = []
  lstTiposFolios: BaseDto[] = [];
  propiedadHorizontal = false
  tiposFolios!: TiposFolios

  /** Se pone en true cuando el guardado terminó bien, para no preguntar al salir */
  private guardado = false

  /** Se muestra debajo del botón cuando se intenta guardar con errores */
  mensajeValidacion: string = ''

  constructor(
    private snackBar: SnackBarService,
    private propiedadesService: PropiedadesService,
    private spinner: NgxSpinnerService,
    private aRoute: ActivatedRoute,
    private subtipoPropiedadService: SubtipoPropiedadService,
    private tiposFoliosServices: TiposFolioService,
    private dialog: MatDialog,
    private elementRef: ElementRef,
    private router: Router,
  ) {
    this.propiedadForm = this.formBuilder.group({
      /* Sin required: el estado lo maneja el sistema y no se edita acá. Si una
         propiedad viniera sin estado, un required dejaría el formulario
         inválido para siempre y sin ningún campo donde corregirlo. */
      EstadoPropiedad: [null],
      ClasePropiedad: [null, Validators.compose([Validators.required])],
      Ubicacion: ['', Validators.compose([Validators.maxLength(200), Validators.required])],
      CodigoCpa: ['', Validators.compose([Validators.maxLength(8)])],
      Antiguedad: ['', Validators.compose([Validators.maxLength(50)])],
      Piso: [''],
      Departamento: [''],
      SuperficieTotal: ['', Validators.compose([Validators.min(0)])],
      SuperficieCubierta: ['', Validators.compose([Validators.min(0)])],
      TiposPropiedad: [null, Validators.compose([Validators.required])],
      SubtiposPropiedad: [null],
      // maxLength(20) sobre un campo con máscara numérica no validaba nada:
      // el valor que llega ya viene sin separadores. min(0) sí sirve.
      Precio: ['', Validators.compose([Validators.min(0)])],
      FechaAlta: [''],
      Observacion: ['', Validators.compose([Validators.maxLength(2000)])],
      TipoFolio: [null, Validators.compose([Validators.required])],
      NroFolio: [''],
      Tomo: [''],
      Numero: [''],
      Horizontal: [false],
      NotificarRecibo: [false],
    }, { validators: [FormUpdatePropiedadComponent.cubiertaMenorOIgualATotal] });

  }

  /**
   * La superficie cubierta no puede ser mayor que la total.
   *
   * Va a nivel de formulario y no de campo porque depende de dos controles: un
   * validador de campo no ve al otro.
   */
  static cubiertaMenorOIgualATotal(form: AbstractControl): ValidationErrors | null {
    const total = form.get('SuperficieTotal')?.value
    const cubierta = form.get('SuperficieCubierta')?.value

    // Si falta alguno de los dos no hay nada que comparar
    if (total === null || total === undefined || total === ''
      || cubierta === null || cubierta === undefined || cubierta === '')
      return null

    return Number(cubierta) > Number(total) ? { cubiertaMayorQueTotal: true } : null
  }

  ngOnInit(): void {
    const routeParams = this.aRoute.snapshot.paramMap;
    this.idPropiedad = Number(routeParams.get('id'));

    this.spinner.show("spPropiedad")
    /* Ya no se pide el listado de estados: el estado dejó de ser un combo y se
       muestra como texto, así que traer el catálogo era una consulta de más en
       cada apertura de la pantalla. */
    forkJoin({
      tiposPropiedad: this.propiedadesService.getAllTiposPropiedad(),
      tiposFolio: this.tiposFoliosServices.getAll(),
      propiedadClases: this.propiedadesService.GetAllClasePropiedad(),

    })
      .subscribe(
        ({ tiposPropiedad, tiposFolio, propiedadClases }) => {

          this.lstTiposPropiedad = tiposPropiedad;
          this.lstTiposFolios = tiposFolio;
          this.lstPropiedadClases = propiedadClases;
          this.spinner.hide("spPropiedad")

        },
        error => {
          this.snackBar.showError(error, "Error");
          this.spinner.hide("spPropiedad")
        });

    this.getPropiedadById(this.idPropiedad)

    this.propiedadesService.setIdPropiedad(this.idPropiedad)

    /* Antes acá había un disable() sobre EstadoPropiedad. Un control
       deshabilitado queda fuera de form.value y de form.valid, así que el
       formulario dependía de leer siempre por controls[...] para no perder el
       dato. El estado lo maneja el sistema, no el usuario: se deja habilitado
       pero no editable desde el HTML (mat-select con [disabled] visual no
       alcanzaba, así que se usa un campo de sólo lectura). */



  }

  /**
   * Vuelve al listado.
   *
   * Se navega con el Router y no con window.history.back(): si el usuario entró
   * pegando la URL directo, el back lo sacaba de la aplicación. Además así el
   * guard de cambios sin guardar se dispara siempre igual.
   */
  /** Ficha de sólo lectura de esta misma propiedad */
  verPropiedad() {
    this.router.navigate(["/propiedades/ver", this.idPropiedad]);
  }

  goBack() {
    this.router.navigate(["/propiedades"]);
  }

  selectTipoFolio(event: any) {
    this.idTipoFolio = event;
  }

  getPropiedadById(id: number) {
    this.spinner.show("spPropiedad")
    this.propiedadesService.getById(id)
      .subscribe(
        data => {
          this.spinner.hide("spPropiedad")
          this.propiedadDto = data
          this.setPropiedadFormulario(this.propiedadDto)
        },
        error => {
          this.spinner.hide("spPropiedad")
        });

  }


  setPropiedadFormulario(propiedadDto: PropiedadDto) {
    this.propiedadForm.controls['EstadoPropiedad'].setValue(isFalsy(propiedadDto.estadoPropiedad) ? null : propiedadDto.estadoPropiedad.id);
    this.propiedadForm.controls['Ubicacion'].setValue(isFalsy(propiedadDto.ubicacion) ? '' : propiedadDto.ubicacion.toUpperCase());
    this.propiedadForm.controls['Piso'].setValue(propiedadDto.piso);
    this.propiedadForm.controls['Departamento'].setValue(propiedadDto.departamento);
    this.propiedadForm.controls['SuperficieTotal'].setValue(propiedadDto.superficieTotal);
    this.propiedadForm.controls['SuperficieCubierta'].setValue(propiedadDto.superficieCubierta);
    this.propiedadForm.controls['Precio'].setValue(propiedadDto.precio);
    this.propiedadForm.controls['CodigoCpa'].setValue(propiedadDto.codigoCpa);
    this.propiedadForm.controls['Antiguedad'].setValue(propiedadDto.antiguedad);
    this.propiedadForm.controls['Observacion'].setValue(propiedadDto.observacion);
    this.propiedadForm.controls['TiposPropiedad'].setValue(isFalsy(propiedadDto.tipoPropiedad) ? null : propiedadDto.tipoPropiedad.id);
    this.propiedadForm.controls['ClasePropiedad'].setValue(isFalsy(propiedadDto.propiedadClase) ? null : propiedadDto.propiedadClase.id);
    this.propiedadForm.controls['TipoFolio'].setValue(isFalsy(propiedadDto.tipoFolio) ? null : propiedadDto.tipoFolio.id);
    this.propiedadForm.controls['Tomo'].setValue(propiedadDto.tomo);
    this.propiedadForm.controls['NroFolio'].setValue(propiedadDto.numeroFolio);
    this.propiedadForm.controls['Numero'].setValue(propiedadDto.numero)
    this.propiedadForm.controls['Horizontal'].setValue(propiedadDto.horizontal)
    this.propiedadForm.controls['NotificarRecibo'].setValue(propiedadDto.notificarRecibo)
    this.idTipoFolio = isFalsy(propiedadDto.tipoFolio) ? null : propiedadDto.tipoFolio.id

    /* Antes se leía propiedadDto.localidad.id sin chequear: una propiedad sin
       localidad tiraba excepción acá y cortaba el resto del armado del
       formulario, así que además quedaban sin setear el subtipo y el tipo. */
    if (!isFalsy(propiedadDto.localidad)) {
      this.localidad.id = propiedadDto.localidad.id

      if (!isFalsy(propiedadDto.localidad.provincia))
        this.localidad.provincia.id = propiedadDto.localidad.provincia.id
    }

    if (!isFalsy(propiedadDto.tipoPropiedad))
      if (propiedadDto.tipoPropiedad.id > 0)
        this.getAllSubtiposPropiedad(propiedadDto.tipoPropiedad.id)

    if (!isFalsy(propiedadDto.subtipoPropiedad))
      this.propiedadForm.controls['SubtiposPropiedad'].setValue(propiedadDto.subtipoPropiedad.id);

    //this.propiedadForm.controls['FechaAlta'].setValue(convierteFecha(propiedadDto.fechaAlta));
    //this.propiedadForm.controls['FechaAlta'].disable();
  }

  getAllSubtiposPropiedad(id_TiposPropiedad: number) {
    this.spinner.show("spPropiedad")
    this.subtipoPropiedadService.getAllByIdTipoPropiedad(id_TiposPropiedad).subscribe(
      data => {
        this.spinner.hide("spPropiedad")
        this.lstSubtiposPropiedad = data
      },
      error => {
        this.spinner.hide("spPropiedad")
        this.snackBar.showError(error, "Error");
      });
  }

  selectTiposPropiedad(envent: any) {
    this.spinner.show("spSubTipoPropiedad")
    this.subtipoPropiedadService.getAllByIdTipoPropiedad(envent.value).subscribe(
      data => {
        this.spinner.hide("spSubTipoPropiedad")
        this.lstSubtiposPropiedad = data
        this.lstSubtiposPropiedad.length === 0 ? this.propiedadForm.controls['SubtiposPropiedad'].disable() : this.propiedadForm.controls['SubtiposPropiedad'].enable()
      },
      error => {
        this.spinner.hide("spSubTipoPropiedad")
        this.snackBar.showError(error, "Error");
      });
  }


  /**
   * El botón ya no se deshabilita.
   *
   * Un botón gris no dice qué falta: el usuario tenía que ir campo por campo
   * adivinando. Ahora se puede apretar siempre, y si algo falta se marcan los
   * campos, se explica qué pasa y se lleva el foco al primero que esté mal.
   */
  onSubmit() {
    this.mensajeValidacion = ''

    const faltaLocalidad = this.localidad.id == 0

    if (this.propiedadForm.invalid || faltaLocalidad) {
      // touched es lo que hace que mat-error se muestre
      this.propiedadForm.markAllAsTouched()

      this.mensajeValidacion = this.armarMensajeDeValidacion(faltaLocalidad)

      this.enfocarPrimerError()

      return
    }

    this.updatePropiedad()
  }

  /** Enumera lo que falta, para no obligar a buscarlo a ojo */
  private armarMensajeDeValidacion(faltaLocalidad: boolean): string {
    const etiquetas: { [control: string]: string } = {
      Ubicacion: 'Ubicación',
      TiposPropiedad: 'Tipo de propiedad',
      ClasePropiedad: 'Clase de propiedad',
      TipoFolio: 'Tipo de folio',
      Precio: 'Precio',
      SuperficieTotal: 'Superficie total',
      SuperficieCubierta: 'Superficie cubierta',
      CodigoCpa: 'Código CPA',
      Antiguedad: 'Antigüedad',
      Observacion: 'Observaciones'
    }

    const faltantes: string[] = []

    Object.keys(this.propiedadForm.controls).forEach(nombre => {
      if (this.propiedadForm.controls[nombre].invalid)
        faltantes.push(etiquetas[nombre] ? etiquetas[nombre] : nombre)
    })

    if (faltaLocalidad)
      faltantes.push('Provincia y Localidad')

    if (this.propiedadForm.errors && this.propiedadForm.errors['cubiertaMayorQueTotal'])
      faltantes.push('La superficie cubierta no puede superar a la total')

    return faltantes.length === 1
      ? 'Falta completar: ' + faltantes[0]
      : 'Faltan completar: ' + faltantes.join(', ')
  }

  /** Lleva el foco al primer campo con error para no hacer buscar */
  private enfocarPrimerError() {
    setTimeout(() => {
      const invalido = this.elementRef.nativeElement.querySelector(
        'input.ng-invalid, mat-select.ng-invalid, textarea.ng-invalid')

      if (invalido && typeof invalido.focus === 'function')
        invalido.focus()

      if (invalido && typeof invalido.scrollIntoView === 'function')
        invalido.scrollIntoView({ block: 'center', behavior: 'smooth' })
    })
  }

  /**
   * Pregunta antes de perder los cambios. La engancha CanDeactivateGuard, que ya
   * usaban contratos y personas.
   */
  permitirSalidaDeRuta(): boolean | Observable<boolean> | Promise<boolean> {
    if (this.guardado || !this.propiedadForm.dirty)
      return true

    const dialogRef = this.dialog.open(DialogCambiosSinGuardarComponent, {
      width: '420px',
      maxWidth: '95vw',
      autoFocus: true,
      disableClose: true
    })

    return dialogRef.afterClosed().pipe(map(salir => salir === true))
  }

  /**
   * Devuelve el valor del campo sin espacios al principio ni al final, y null si
   * queda vacío.
   *
   * Sin esto se guardaban cosas como "  " o "" en columnas que aceptan NULL: en
   * las pantallas se ven igual que un vacío, pero rompen los filtros y las
   * comparaciones, porque '' <> NULL.
   */
  private texto(nombreControl: string, mayusculas: boolean = false): string | undefined {
    const valor = this.propiedadForm.controls[nombreControl].value

    if (valor === null || valor === undefined)
      return undefined

    const limpio = valor.toString().trim()

    if (limpio === '')
      return undefined

    return mayusculas ? limpio.toUpperCase() : limpio
  }

  setUpdatePropiedadRequest(): UpdatePropiedadRequest {
    let updatePropiedadRequest = new UpdatePropiedadRequest
    updatePropiedadRequest.id = this.idPropiedad
    updatePropiedadRequest.id_EstadoPropiedad = this.propiedadForm.controls['EstadoPropiedad'].value;
    /* El text-transform del HTML es sólo visual: se ve en mayúsculas pero se
       guardaba tal cual lo tipearon. El alta sí hacía toUpperCase y la edición
       no, así que editar una propiedad le bajaba las mayúsculas a la dirección.
       Ahora las dos pantallas normalizan igual. */
    updatePropiedadRequest.ubicacion = this.texto('Ubicacion', true);
    updatePropiedadRequest.departamento = this.texto('Departamento', true);
    updatePropiedadRequest.piso = this.texto('Piso', true);
    updatePropiedadRequest.id_Localidad = this.localidad.id
    updatePropiedadRequest.id_PropiedadClase = this.propiedadForm.controls['ClasePropiedad'].value;
    updatePropiedadRequest.idTipoPropiedad = this.propiedadForm.controls['TiposPropiedad'].value;
    updatePropiedadRequest.idSubtipoPropiedad = this.propiedadForm.controls['SubtiposPropiedad'].value;
    updatePropiedadRequest.superficieTotal = this.propiedadForm.controls['SuperficieTotal'].value;
    updatePropiedadRequest.superficieCubierta = this.propiedadForm.controls['SuperficieCubierta'].value;
    updatePropiedadRequest.precio = this.propiedadForm.controls['Precio'].value;
    updatePropiedadRequest.antiguedad = this.texto('Antiguedad');
    updatePropiedadRequest.codigoCpa = this.texto('CodigoCpa');
    updatePropiedadRequest.observacion = this.texto('Observacion');
    updatePropiedadRequest.horizontal = this.propiedadForm.controls['Horizontal'].value;
    updatePropiedadRequest.notificarRecibo = this.propiedadForm.controls['NotificarRecibo'].value;
    updatePropiedadRequest.activo = this.propiedadDto.activo
    updatePropiedadRequest.idTipoFolio = this.propiedadForm.controls['TipoFolio'].value
    updatePropiedadRequest.nroFolio = this.texto('NroFolio')

    /* El if preguntaba por updatePropiedadRequest.tomo, que en ese punto todavía
       no se había asignado nunca: siempre valía undefined, la condición siempre
       daba verdadero y la rama del "0" no se ejecutaba jamás. Ahora se mira el
       valor del formulario, que es lo que se quería preguntar. */
    if (updatePropiedadRequest.idTipoFolio == TiposFolios.FolioPersonalCronologico) {
      updatePropiedadRequest.tomo = this.texto('Tomo') ?? "0"
      updatePropiedadRequest.numero = this.texto('Numero') ?? "0"
    }
    else {
      updatePropiedadRequest.tomo = ""
      updatePropiedadRequest.numero = ""
    }

    return updatePropiedadRequest
  }

  updatePropiedad() {
    this.spinner.show("spPropiedad")
    const updatePropiedadRequest = this.setUpdatePropiedadRequest();
    this.propiedadesService.update(updatePropiedadRequest).subscribe(
      data => {
        this.spinner.hide("spPropiedad")
        this.snackBar.showSuccess('Se actualizo la propiedad de manera correcta.', "");

        // Sin esto el guard seguiría avisando de cambios ya guardados
        this.guardado = true
        this.mensajeValidacion = ''
        this.propiedadForm.markAsPristine()
      },
      error => {
        this.spinner.hide("spPropiedad")
        this.snackBar.showError(error, "Error");
      });
  }


  onReset() {
    this.propiedadForm.reset();
  }

  seleccionaLocalidad(data: LocalidadDto) {
    this.localidad.id = data!= null?data.id: 0
  }

  trackByItems(index: number, item: any): any { return item; }

}
